import { FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision";
import { BACKGROUND_IMAGE_URLS } from "./ChatCallPanel.constants";

const WASM_PATH = "/mediapipe/tasks-vision/wasm";
const MODEL_PATH = "/models/selfie_segmenter_landscape.tflite";
const TARGET_FPS = 15;
const MASK_THRESHOLD = 0.48;
const EFFECT_READY_TIMEOUT_MS = 8000;

let segmenterPromise = null;
let latestSegmentationTimestamp = 0;

const getNextSegmentationTimestamp = timestamp => {
    const nextTimestamp = Number.isFinite(timestamp)
        ? Math.ceil(timestamp)
        : Math.ceil(window.performance.now());
    latestSegmentationTimestamp = Math.max(
        nextTimestamp,
        latestSegmentationTimestamp + 1
    );
    return latestSegmentationTimestamp;
};

const isFirefox = () =>
    /firefox/i.test(navigator.userAgent || "");

const isSafari = () =>
    /safari/i.test(navigator.userAgent || "") &&
    !/chrome|chromium|crios|fxios|android/i.test(navigator.userAgent || "");

const getSegmenter = async () => {
    if (!segmenterPromise) {
        segmenterPromise = (async () => {
            const vision =
                await FilesetResolver.forVisionTasks(WASM_PATH);

            const createSegmenter = delegate =>
                ImageSegmenter.createFromOptions(vision, {
                    baseOptions: {
                        modelAssetPath: MODEL_PATH,
                        delegate
                    },
                    runningMode: "video",
                    outputCategoryMask: false,
                    outputConfidenceMasks: true
                });

            // MediaPipe's WebGL delegate is unreliable for captured canvas video in Firefox and Safari.
            if (isFirefox() || isSafari()) {
                return createSegmenter("CPU");
            }

            try {
                return await createSegmenter("GPU");
            } catch (error) {
                console.warn(
                    "GPU virtual background segmentation unavailable; falling back to CPU.",
                    error
                );

                return createSegmenter("CPU");
            }
        })().catch(error => {
            segmenterPromise = null;
            throw error;
        });
    }

    return segmenterPromise;
};

const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });
};

const waitForVideoFrame = (video) => {
    if (video.videoWidth && video.videoHeight) {
        return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
            cleanup();
            reject(new Error("Timed out waiting for camera video."));
        }, 5000);

        const cleanup = () => {
            window.clearTimeout(timeoutId);
            video.removeEventListener("loadedmetadata", handleReady);
            video.removeEventListener("loadeddata", handleReady);
            video.removeEventListener("playing", handleReady);
            video.removeEventListener("error", handleError);
        };

        const handleReady = () => {
            if (!video.videoWidth || !video.videoHeight) {
                return;
            }

            cleanup();
            resolve();
        };

        const handleError = () => {
            cleanup();
            reject(new Error("Unable to read camera video."));
        };

        video.addEventListener("loadedmetadata", handleReady);
        video.addEventListener("loadeddata", handleReady);
        video.addEventListener("playing", handleReady);
        video.addEventListener("error", handleError);
    });
};

const resolveBackgroundImage = async (effect, customBackgroundUrl = "") => {
    const src = effect === "custom"
        ? customBackgroundUrl
        : BACKGROUND_IMAGE_URLS[effect];

    return src ? loadImage(src) : null;
};

const drawCoverImage = (context, image, width, height) => {
    const sourceWidth = image.videoWidth || image.naturalWidth || image.width;
    const sourceHeight = image.videoHeight || image.naturalHeight || image.height;
    const scale = Math.max(width / sourceWidth, height / sourceHeight);
    const drawWidth = sourceWidth * scale;
    const drawHeight = sourceHeight * scale;
    const offsetX = (width - drawWidth) / 2;
    const offsetY = (height - drawHeight) / 2;

    context.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

const drawBlurredBackground = (
    context,
    sourceVideo,
    width,
    height,
    strong,
    blurCanvas,
    blurContext
) => {
    if (!isSafari()) {
        context.save();
        context.filter = strong ? "blur(18px)" : "blur(10px)";
        drawCoverImage(context, sourceVideo, width, height);
        context.restore();
        return;
    }

    // Safari does not reliably apply 2D context filters to canvas capture streams.
    const scale = strong ? 0.035 : 0.07;
    blurCanvas.width = Math.max(20, Math.round(width * scale));
    blurCanvas.height = Math.max(12, Math.round(height * scale));
    blurContext.imageSmoothingEnabled = true;
    blurContext.imageSmoothingQuality = "high";
    drawCoverImage(blurContext, sourceVideo, blurCanvas.width, blurCanvas.height);
    context.save();
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(blurCanvas, 0, 0, width, height);
    context.restore();
};

const drawBackground = (
    context,
    effect,
    backgroundImage,
    sourceVideo,
    width,
    height,
    blurCanvas,
    blurContext
) => {
    if (["blur", "strong-blur"].includes(effect)) {
        drawBlurredBackground(
            context,
            sourceVideo,
            width,
            height,
            effect === "strong-blur",
            blurCanvas,
            blurContext
        );
        return;
    }

    if (effect === "gradient" || effect === "preset") {
        const gradient = context.createLinearGradient(0, 0, width, height);
        gradient.addColorStop(0, "#0B66E4");
        gradient.addColorStop(0.48, "#5B6EF5");
        gradient.addColorStop(1, "#7C3AED");
        context.fillStyle = gradient;
        context.fillRect(0, 0, width, height);
        return;
    }

    if (backgroundImage) {
        drawCoverImage(context, backgroundImage, width, height);
        return;
    }

    context.fillStyle = "#111827";
    context.fillRect(0, 0, width, height);
};

const renderMask = (mask, canvas, context) => {
    const width = mask.width;
    const height = mask.height;
    const maskData = mask.getAsFloat32Array();
    const imageData = context.createImageData(width, height);

    for (let index = 0; index < maskData.length; index++) {
        const alpha = maskData[index] >= MASK_THRESHOLD ? 255 : Math.round(maskData[index] * 255);
        const offset = index * 4;
        imageData.data[offset] = 255;
        imageData.data[offset + 1] = 255;
        imageData.data[offset + 2] = 255;
        imageData.data[offset + 3] = alpha;
    }

    canvas.width = width;
    canvas.height = height;
    context.putImageData(imageData, 0, 0);
};

const getPersonMask = (segmenter, result) => {
    const masks = result.confidenceMasks || [];
    if (!masks.length) {
        return null;
    }

    const labels = typeof segmenter.getLabels === "function" ? segmenter.getLabels() : [];
    const personIndex = labels.findIndex(label => /person|selfie|human/i.test(label));
    return masks[personIndex >= 0 ? personIndex : masks.length - 1] || masks[0];
};

const closeSegmentationMasks = (result) => {
    result.confidenceMasks?.forEach(mask => mask.close?.());
    result.categoryMask?.close?.();
};

const getOutputSize = video => {
    const scale = isSafari()
        ? Math.min(1, 640 / video.videoWidth)
        : 1;

    return {
        width: Math.max(1, Math.round(video.videoWidth * scale)),
        height: Math.max(1, Math.round(video.videoHeight * scale))
    };
};

export const backgroundReplacementIsSupported = () => {
    return Boolean(navigator.mediaDevices?.getUserMedia) &&
        typeof HTMLCanvasElement !== "undefined" &&
        typeof HTMLCanvasElement.prototype.captureStream === "function" &&
        typeof MediaStream !== "undefined";
};

export const createVirtualBackgroundVideoTrack = async ({
    customBackgroundUrl = "",
    effect,
    sourceTrack
}) => {
    if (!sourceTrack || effect === "none" || !backgroundReplacementIsSupported()) {
        return null;
    }

    const sourceStream = new MediaStream([sourceTrack]);
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = sourceStream;
    await video.play();
    await waitForVideoFrame(video);

    const segmenter = await getSegmenter();
    let currentEffect = effect;
    let currentCustomBackgroundUrl = customBackgroundUrl;

    let backgroundImage = await resolveBackgroundImage(
        currentEffect,
        currentCustomBackgroundUrl
    );

    const outputCanvas = document.createElement("canvas");
    const outputContext = outputCanvas.getContext("2d");
    const personCanvas = document.createElement("canvas");
    const personContext = personCanvas.getContext("2d");
    const maskCanvas = document.createElement("canvas");
    const maskContext = maskCanvas.getContext("2d");
    const blurCanvas = document.createElement("canvas");
    const blurContext = blurCanvas.getContext("2d");
    let animationFrameId = 0;
    let lastFrameAt = 0;
    let stopped = false;
    let effectUpdateId = 0;
    const effectFrameWaiters = new Map();
    const targetFps = isSafari() ? 12 : TARGET_FPS;

    if (!outputContext || !personContext || !maskContext || !blurContext) {
        throw new Error("Canvas rendering is not available for virtual backgrounds.");
    }

    const initialOutputSize = getOutputSize(video);
    outputCanvas.width = initialOutputSize.width;
    outputCanvas.height = initialOutputSize.height;
    outputContext.drawImage(video, 0, 0, outputCanvas.width, outputCanvas.height);

    const waitForEffectFrame = updateId => new Promise((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
            effectFrameWaiters.delete(updateId);
            reject(new Error("Timed out while applying the background effect."));
        }, EFFECT_READY_TIMEOUT_MS);

        effectFrameWaiters.set(updateId, {
            reject: error => {
                window.clearTimeout(timeoutId);
                reject(error);
            },
            resolve: () => {
                window.clearTimeout(timeoutId);
                resolve();
            }
        });
    });

    const markEffectFrameReady = updateId => {
        const waiter = effectFrameWaiters.get(updateId);
        if (!waiter) {
            return;
        }

        effectFrameWaiters.delete(updateId);
        waiter.resolve();
    };

    const rejectEffectFrameWaiters = () => {
        effectFrameWaiters.forEach(waiter => {
            waiter.reject(new Error("Background effect processing stopped."));
        });
        effectFrameWaiters.clear();
    };

    const renderFrame = (timestamp) => {
        if (stopped) {
            return;
        }

        animationFrameId = window.requestAnimationFrame(renderFrame);
        if (timestamp - lastFrameAt < 1000 / targetFps || !video.videoWidth || !video.videoHeight) {
            return;
        }
        lastFrameAt = timestamp;

        const { width, height } = getOutputSize(video);
        outputCanvas.width = width;
        outputCanvas.height = height;
        personCanvas.width = width;
        personCanvas.height = height;

        if (currentEffect === "none") {
            outputContext.drawImage(video, 0, 0, width, height);
            markEffectFrameReady(effectUpdateId);
            return;
        }

        try {
            const result = segmenter.segmentForVideo(
                video,
                getNextSegmentationTimestamp(timestamp)
            );
            const personMask = getPersonMask(segmenter, result);
            if (!personMask) {
                outputContext.drawImage(video, 0, 0, width, height);
                closeSegmentationMasks(result);
                return;
            }

            renderMask(personMask, maskCanvas, maskContext);
            drawBackground(
                outputContext,
                currentEffect,
                backgroundImage,
                video,
                width,
                height,
                blurCanvas,
                blurContext
            );
            personContext.clearRect(0, 0, width, height);
            personContext.drawImage(video, 0, 0, width, height);
            personContext.globalCompositeOperation = "destination-in";
            personContext.drawImage(maskCanvas, 0, 0, width, height);
            personContext.globalCompositeOperation = "source-over";
            outputContext.drawImage(personCanvas, 0, 0, width, height);
            closeSegmentationMasks(result);
            markEffectFrameReady(effectUpdateId);
        } catch (error) {
            console.warn("Unable to render virtual background frame.", error);
            outputContext.drawImage(video, 0, 0, width, height);
        }
    };

    animationFrameId = window.requestAnimationFrame(renderFrame);
    const [processedTrack] = outputCanvas.captureStream(targetFps).getVideoTracks();
    processedTrack.__meBusinessVirtualBackground = true;
    processedTrack.__meBusinessVirtualBackgroundSourceTrack = sourceTrack;
    processedTrack.__meBusinessSetVirtualBackground =
    async ({
        effect: nextEffect,
        customBackgroundUrl: nextCustomBackgroundUrl = ""
    }) => {
        const updateId = ++effectUpdateId;
        const nextBackgroundImage = await resolveBackgroundImage(
            nextEffect,
            nextCustomBackgroundUrl
        );
        if (stopped || updateId !== effectUpdateId) {
            return false;
        }

        currentEffect = nextEffect;
        currentCustomBackgroundUrl = nextCustomBackgroundUrl;
        backgroundImage = nextBackgroundImage;
        await waitForEffectFrame(updateId);
        return true;
    };
    processedTrack.__meBusinessVirtualBackgroundCleanup = () => {
        stopped = true;
        window.cancelAnimationFrame(animationFrameId);
        rejectEffectFrameWaiters();
        video.pause();
        video.srcObject = null;
        sourceTrack.stop();
        processedTrack.stop();
    };

    try {
        await waitForEffectFrame(effectUpdateId);
    } catch (error) {
        processedTrack.__meBusinessVirtualBackgroundCleanup();
        throw error;
    }

    return processedTrack;
};

export const applyVirtualBackgroundToStream = async ({
    customBackgroundUrl,
    effect,
    stream
}) => {
    if (!stream || effect === "none") {
        return stream;
    }

    const sourceTrack = stream.getVideoTracks()
        .find(track => !track.__meBusinessPlaceholder);
    if (!sourceTrack) {
        return stream;
    }

    if (sourceTrack.__meBusinessVirtualBackground) {
        return stream;
    }

    const processedTrack = await createVirtualBackgroundVideoTrack({
        customBackgroundUrl: customBackgroundUrl || "",
        effect,
        sourceTrack
    });
    if (!processedTrack) {
        return stream;
    }

    return new MediaStream([
        ...stream.getAudioTracks(),
        processedTrack
    ]);
};
